/**
 * Consent Service — Enterprise-grade GDPR/CCPA consent manager for KnightSteps.
 *
 * Handles consent persistence, 180-day TTL expiration, Global Privacy Control (GPC)
 * signals, and reactive event notifications.
 */

export const STORAGE_KEY = 'ks_consent_preferences';
export const CONSENT_VERSION = '1.0';
export const CONSENT_TTL_DAYS = 180; // 6 months expiration per GDPR guidelines

/**
 * Checks whether Global Privacy Control (GPC) or Do Not Track (DNT) is signaled.
 */
export const isGpcEnabled = () => {
  if (typeof window === 'undefined') return false;
  return (
    navigator.globalPrivacyControl === true ||
    window.doNotTrack === '1' ||
    navigator.doNotTrack === '1' ||
    navigator.msDoNotTrack === '1'
  );
};

/**
 * Retrieves valid, unexpired consent preferences from localStorage.
 * Returns null if no consent has been recorded or if consent has expired.
 */
export const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);

    // Validate structure
    if (!data || typeof data !== 'object') return null;

    // Check expiration (180-day TTL)
    const timestamp = data.timestamp || 0;
    const ageMs = Date.now() - timestamp;
    const maxAgeMs = CONSENT_TTL_DAYS * 24 * 60 * 60 * 1000;

    if (ageMs > maxAgeMs) {
      // Consent expired, prompt user again
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      essential: true, // Always true
      functional: Boolean(data.functional),
      analytics: Boolean(data.analytics),
      timestamp: data.timestamp,
      version: data.version || CONSENT_VERSION,
      gpcHonored: Boolean(data.gpcHonored),
    };
  } catch (err) {
    console.warn('[ConsentService] Unable to read consent from localStorage:', err);
    return null;
  }
};

/**
 * Persists user consent choices to localStorage and dispatches a system event.
 */
export const saveConsent = ({ functional = false, analytics = false }) => {
  if (typeof window === 'undefined') return;

  const gpcActive = isGpcEnabled();

  const payload = {
    essential: true,
    functional: Boolean(functional),
    analytics: gpcActive ? false : Boolean(analytics), // Enforce GPC for analytics if active
    timestamp: Date.now(),
    version: CONSENT_VERSION,
    gpcHonored: gpcActive,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn('[ConsentService] Unable to save consent to localStorage:', err);
  }

  // Reactive notification across components and services
  window.dispatchEvent(new CustomEvent('ks:consent-updated', { detail: payload }));
  return payload;
};

/**
 * Queries whether consent is granted for a specific category.
 * @param {'essential' | 'functional' | 'analytics'} category
 */
export const hasConsent = (category) => {
  if (category === 'essential') return true;

  const stored = getStoredConsent();
  if (!stored) return false; // Privacy by default: false until explicitly opted-in

  return Boolean(stored[category]);
};

/**
 * Programmatically triggers the Consent Modal to reopen (e.g. from footer or settings).
 */
export const openConsentModal = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('ks:open-consent-modal'));
};
